import { RingBuffer } from "ring-buffer-ts";
import { Object3D, Vector3 } from "three";
import { EdgeStructure, Vector3FromEdgePoint } from "../ik/metadata";

import { BoneLengths, JointPositions } from "../types";
import { SkeletalFrame, SkeletalPosition } from "../types/statsApi";
import { findCentroid, JointMap } from "./util";

/**
 * Build JointPositions for the mesh armature
 */
export function generateRigPoints(
  root: Object3D,
  structure?: EdgeStructure
): JointPositions {
  const points = new JointPositions();
  const worldPos = new Vector3();

  if (structure) {
    for (const name in structure) {
      const entry = structure[name];
      const point = new Vector3(0, 0, 0);
      Vector3FromEdgePoint(point, root, entry.end);
      point.set(point.x, point.y, point.z);

      points.set(name, point);
    }
  } else {
    root.traverse((obj: Object3D) => {
      if (obj.type == "Bone") {
        // NB: point of bone is actually at average of child three.js Bone
        if (obj.children.length != 0) {
          const point = new Vector3(0, 0, 0);
          for (const child of obj.children) {
            child.getWorldPosition(worldPos);
            point.add(worldPos);
          }
          // MLB coord system is different from three.js
          point.divideScalar(obj.children.length);
          point.set(point.x, point.y, point.z);

          points.set(obj.name, point);
        } else {
          obj.getWorldPosition(worldPos);
          points.set(obj.name, new Vector3(worldPos.x, worldPos.y, worldPos.z));
        }
      }
    });
  }

  return points;
}

const midhip = new Vector3();
const face = new Vector3();
const feet = new Vector3();

export function calculateBoneLengths(
  joints: JointPositions,
  warn = true
): BoneLengths | null {
  const lengths: BoneLengths = {};
  let success = true;

  // handed bone lengths
  const handedJoints = [
    ["Shoulder", "Elbow", "Wrist"],
    ["Hip", "Knee", "Ankle"],
  ];

  for (const chain of handedJoints) {
    for (let i = 0; i < chain.length - 1; i++) {
      const headName = chain[i];
      const tailName = chain[i + 1];

      const lHead = joints.get("L" + headName);
      const lTail = joints.get("L" + tailName);
      const rHead = joints.get("R" + headName);
      const rTail = joints.get("R" + tailName);

      if (lHead && lTail && rHead && rTail) {
        lengths[chain[i]] =
          (lHead.distanceTo(lTail) + rHead.distanceTo(rTail)) / 2;
      } else {
        if (warn) {
          console.warn("issue measuring handed joints", joints);
          console.trace();
        }
        success = false;
        break;
      }
    }

    if (!success) {
      break;
    }
  }

  const lHip = joints.get("LHip");
  const rHip = joints.get("RHip");
  const neck = joints.get("Neck");

  if (lHip && rHip && neck) {
    // spine bone length
    midhip.addVectors(lHip, rHip).divideScalar(2);
    lengths["Spine"] = midhip.distanceTo(neck);

    // hip bone width
    lengths["Pelvis"] = lHip.distanceTo(rHip);
  } else {
    if (warn) {
      console.warn("issue measuring spine and pelvis", joints);
    }
    success = false;
  }

  // shoulder width
  const lShoulder = joints.get("LShoulder");
  const rShoulder = joints.get("RShoulder");

  if (lShoulder && rShoulder) {
    lengths["Back"] = lShoulder.distanceTo(rShoulder);
  } else {
    if (warn) {
      console.warn("issue measuring back", joints);
    }
    success = false;
  }

  // neck length & height
  const rEye = joints.get("REye");
  const lEye = joints.get("LEye");
  const rEar = joints.get("REar");
  const lEar = joints.get("LEar");
  const nose = joints.get("Nose");
  const lAnkle = joints.get("LAnkle");
  const rAnkle = joints.get("RAnkle");

  if (rEye && lEye && rEar && lEar && nose && neck && lAnkle && rAnkle) {
    findCentroid([rEye, lEye, rEar, lEar, nose], face);
    findCentroid([lAnkle, rAnkle], feet);

    face.y = neck.y;

    lengths["Head"] = face.distanceTo(neck);
    // lengths["Height"] = face.distanceTo(feet);
  } else {
    if (warn) {
      console.warn("issue measuring neck and height", joints);
    }
    success = false;
  }

  return success ? lengths : null;
}

export class BoneStats {
  mean: BoneLengths;
  m2: BoneLengths;
  vals: { [key: string]: number[] };
  frameCount: number;
  maxFrameCount: number;
  maxHistory: number;

  fallBackJointMap = new JointMap();

  constructor(maxFrameCount = -1, maxHistory = -1) {
    this.mean = {};
    this.m2 = {};
    this.vals = {};
    this.frameCount = 0;
    this.maxFrameCount = maxFrameCount;
    this.maxHistory = maxHistory;
  }

  reset() {
    this.mean = {};
    this.m2 = {};
    this.vals = {};
    this.frameCount = 0;
  }

  /**
   * Uses welford's algorithm for accumulating variance
   * https://en.wikipedia.org/wiki/Algorithms_for_calculating_variance
   */
  update(newLengths: BoneLengths): boolean {
    if (this.maxFrameCount !== -1 && this.frameCount >= this.maxFrameCount) {
      return false;
    }

    this.frameCount++;
    for (const name in newLengths) {
      if (!this.mean.hasOwnProperty(name) || this.frameCount === 1) {
        this.mean[name] = 0;
        this.m2[name] = 0;
        this.vals[name] = [];
      }

      const newVal = newLengths[name];
      const delta = newVal - this.mean[name];

      this.mean[name] += delta / this.frameCount;
      this.m2[name] += (newVal - this.mean[name]) * delta;

      if (this.maxHistory != 0) {
        this.vals[name].push(newVal);
        if (this.maxHistory > 0) {
          if (this.vals[name].length > this.maxHistory) {
            this.vals[name].shift();
          }
        }
      }
    }

    return true;
  }

  processPlay(
    frames: RingBuffer<SkeletalFrame> | SkeletalFrame[],
    posId: number,
    extractPosition: (
      frame: SkeletalFrame,
      id: number
    ) => SkeletalPosition | undefined,
    jointMap?: JointMap,
    trackId?: number
  ) {
    if (!jointMap) {
      jointMap = this.fallBackJointMap;
    }
    this.reset();
    let present = 0;
    let missing = 0;

    const nframes =
      frames instanceof RingBuffer ? frames.getBufferLength() : frames.length;
    for (let i = 0; i < nframes; i++) {
      const frame = frames instanceof RingBuffer ? frames.get(i) : frames[i];
      if (!frame) continue;

      const position = extractPosition(frame, posId);
      if (!position) {
        missing++;
        continue;
      }
      if (trackId != undefined && position.track_id != trackId) {
        continue;
      }

      const points = jointMap.asJointPositions(position);

      const lengths = calculateBoneLengths(points);

      if (lengths) {
        this.update(lengths);
      }

      present++;
    }

    if (missing != 0) {
      // TODO: add this back in under debug flag?
      // console.warn(
      //   `${missing} missing frames for pos ${posId} ` +
      //     `(total ${missing + present})`
      // );
    }
  }

  getMean(name: string): number {
    return this.mean[name];
  }

  // TODO: implement me
  getMedian(name: string): number | undefined {
    return undefined;
  }

  getVariance(name: string): number {
    if (!this.m2.hasOwnProperty(name)) {
      return 0;
    }

    return this.m2[name] / this.frameCount;
  }

  getVals(name: string): number[] {
    return this.vals[name];
  }

  keys(): string[] {
    return Object.keys(this.mean);
  }
}

interface BoneErrors {
  total: number;
  [key: string]: number;
}

export function overallBoneError(
  root: Object3D,
  rawStats: BoneStats,
  scale?: number,
  structure?: EdgeStructure
): BoneErrors {
  if (rawStats.frameCount <= 0) {
    console.error("invalid frame count");
    return { total: -1 };
  }

  let rigPoints;
  if (scale != undefined) {
    const origScale = root.scale;
    root.scale.set(scale, scale, scale);
    rigPoints = generateRigPoints(root, structure);
    root.scale.copy(origScale);
  } else {
    rigPoints = generateRigPoints(root, structure);
  }

  const riggedLengths = calculateBoneLengths(rigPoints);
  if (!riggedLengths) {
    console.error("invalid rigged lengths");
    return { total: -1 };
  }

  const errors: BoneErrors = { total: 0 };

  for (const name in riggedLengths) {
    const mean = rawStats.getMean(name);
    if (mean) {
      const boneError = riggedLengths[name] - mean;
      errors[name] = boneError;
      // console.log(`bone error ${name}: ${boneError}`);
      errors.total += Math.abs(boneError);
    } else {
      console.error(`unable to generate mean for ${name}`);
    }
  }

  return errors;
}

/**
 * Finds local minimum for global model scale -> bone length error
 *
 * @param root model root
 * @param rawLengths Hawkeye raw limb length stats
 * @param start initial guess for scale
 * @param stepSize used in optimiser
 * @returns
 */
export function optimiseGlobalScale(
  root: Object3D,
  rawStats: BoneStats,
  structure?: EdgeStructure,
  start = 1,
  stepSize = 0.05
): { scale: number; error: number } {
  // TODO: do we want to try and do something beyond finding local min here?
  const maxIters = 100;
  const deltaTol = 1e-7;
  let iters = maxIters;

  let scale = start;
  let errors: BoneErrors = { total: -1 };

  while (iters--) {
    errors = overallBoneError(root, rawStats, scale, structure);
    const nextErrors = overallBoneError(
      root,
      rawStats,
      scale + stepSize,
      structure
    );

    const delta = errors.total - nextErrors.total;

    if (Math.abs(delta) < deltaTol) {
      break;
    }

    scale += delta;
  }

  return { scale: scale, error: errors.total };
}
